import useSWR, { useSWRConfig } from "swr";
import useSWRMutation from "swr/mutation";
import Page403 from "@/components/zthd/403";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { refreshInbox } from "@/hooks/inbox";
import { postFetcher } from "@/lib/swr";
import { toastError } from "@/lib/toast";
import type { PendingAccessRequest, Prd, PrdAccessRequest } from "@/types/domain";

// 403 on a PRD (WED-276): ask the owner for read access. `id` is whatever the
// route carried — /e/:id work item, document id or short ID; the server
// resolves it without requiring visibility. Approval lands in the owner's Inbox.
export const PrdNoAccess = ({ id }: { id: string }) => {
  const { toast } = useToast();
  const path = `/prds/${encodeURIComponent(id)}/access-requests`;
  const { data, mutate } = useSWR<PrdAccessRequest>([`${path}/mine`]);
  const { trigger, isMutating } = useSWRMutation(path, postFetcher<PrdAccessRequest>, {
    onSuccess: (saved) => mutate(saved, { revalidate: false }),
    onError: (e: Error) => toastError(toast, "申请失败", e),
  });
  const status = data?.status ?? null;
  const action =
    status === "pending" ? (
      <p role="status" className="text-sm text-muted-foreground">已申请，等待 owner 处理</p>
    ) : status === "approved" ? (
      <p role="status" className="text-sm text-muted-foreground">申请已通过，刷新页面即可查看</p>
    ) : (
      <>
        {status === "rejected" && (
          <p role="status" className="text-sm text-muted-foreground">上次申请未通过，可以再次申请</p>
        )}
        <Button disabled={isMutating} onClick={() => trigger({}).catch(() => {})}>
          {isMutating ? "申请中…" : "申请查看"}
        </Button>
      </>
    );
  return <Page403 action={action} />;
};

const accessRequestsPath = (id: string) => `/prds/${encodeURIComponent(id)}/access-requests`;

// Approve / Reject pair shared by the inbox row and the detail dialog:
// approve adds the requester to the viewer list, reject closes the request.
// `onDone` refreshes whatever the caller shows after either outcome.
export const AccessRequestButtons = ({
  approveUrl,
  rejectUrl,
  onDone,
}: {
  approveUrl: string | null;
  rejectUrl: string | null;
  onDone: () => void;
}) => {
  const { toast } = useToast();
  const options = {
    onSuccess: onDone,
    onError: (e: Error) => toastError(toast, "处理申请失败", e),
  };
  const approve = useSWRMutation(approveUrl ?? "", postFetcher, options);
  const reject = useSWRMutation(rejectUrl ?? "", postFetcher, options);
  const busy = approve.isMutating || reject.isMutating;
  return (
    <div className="flex gap-2">
      <Button size="sm" variant="outline" disabled={busy || !approveUrl}
        onClick={(e) => { e.stopPropagation(); approve.trigger({}).catch(() => {}); }}>
        {approve.isMutating ? "Approving…" : "Approve"}
      </Button>
      <Button size="sm" variant="ghost" disabled={busy || !rejectUrl}
        onClick={(e) => { e.stopPropagation(); reject.trigger({}).catch(() => {}); }}>
        {reject.isMutating ? "Rejecting…" : "Reject"}
      </Button>
    </div>
  );
};

const AccessRequestRow = ({ prd, request }: { prd: Prd; request: PendingAccessRequest }) => {
  const { mutate } = useSWRConfig();
  const base = `${accessRequestsPath(prd.id)}/${request.id}`;
  // Reload the list and this detail (its pending count); the inbox has its own refresher.
  const paths = [accessRequestsPath(prd.id), `/prds/${prd.id}`, `/prds/${prd.documentId ?? prd.id}`];
  const onDone = () => {
    mutate((key) => Array.isArray(key) && paths.includes(key[0]));
    refreshInbox();
  };
  return (
    <li className="flex items-center gap-2">
      <span className="min-w-0 flex-1 truncate">{request.userName}</span>
      <AccessRequestButtons approveUrl={`${base}/approve`} rejectUrl={`${base}/reject`} onDone={onDone} />
    </li>
  );
};

// Approvers handle a PRD's pending access requests from its detail page
// (reachable on phones, where the inbox rows hide inline actions).
export const AccessRequestsDialog = ({
  prd,
  onOpenChange,
}: {
  prd: Prd;
  onOpenChange: (open: boolean) => void;
}) => {
  const { data, isLoading } = useSWR<PendingAccessRequest[]>([accessRequestsPath(prd.id)]);
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>访问申请</DialogTitle>
          <DialogDescription>批准后对方加入只读查看名单。</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">加载中…</p>
        ) : data?.length ? (
          <ul aria-label="待处理的访问申请" className="flex flex-col gap-2">
            {data.map((request) => (
              <AccessRequestRow key={request.id} prd={prd} request={request} />
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">没有待处理的申请。</p>
        )}
      </DialogContent>
    </Dialog>
  );
};
